import { UserAddOutlined } from "@ant-design/icons";
import { Users } from "lucide-react";
import { Button, Form, FormProps, Input, message, Select } from "antd";
import React, { useState } from "react";
import { Folder } from "../../redux/interfaces/folder.types.ts";
import { useAppDispatch } from "../../redux/hooks.ts";
import { addSubfolderToFolder } from "../../redux/actions/templatesActions.tsx";
import { fetchFolderById } from "../../redux/actions/vaultActions.tsx";
import { setActiveFolderAsync } from "../../redux/actions/folderActions.tsx";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";
import { getOwnerNameFromForms } from "../../utils/subfolders/ownerName.ts";

interface Props {
  folder: Folder;
}

type FormValues = {
  template_slug: string;
  full_name?: string;
  represents_subfolder_id?: string;
};

// Additional participants use the lightweight "-cja" templates and can be added
// several times for the same role (one subfolder per person).
const PARTICIPANT_OPTIONS = [
  { value: "tenant-pf-cja", label: "Inquilino PF (adicional)" },
  { value: "tenant-pm-cja", label: "Inquilino PM (adicional)" },
  { value: "guarantor-pf-cja", label: "Fiador PF (adicional)" },
  { value: "guarantor-pm-cja", label: "Fiador PM (adicional)" },
  // MB-431: SECONDARY landlords (2nd+) use identity-only templates. The principal
  // landlord + rental terms stay in `lease-contract-pf/pm`; extras share those terms.
  { value: "landlord-pf", label: "Propietario PF (adicional)" },
  { value: "landlord-pm", label: "Propietario PM (adicional)" },
  // Owner representatives: each one represents a specific owner (see the dropdown below).
  { value: "owner-representative", label: "Representante del Propietario PF" },
  { value: "owner-representative-pm", label: "Representante del Propietario PM" },
];

// Slugs whose subfolders are "owners" a representative can point at.
const OWNER_SLUG_PREFIXES = ["lease-contract", "landlord"];

const isRepresentativeSlug = (slug?: string) =>
  Boolean(slug && slug.startsWith("owner-representative"));

export const AddParticipantToFolder: React.FC<Props> = ({ folder }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const selectedSlug = Form.useWatch("template_slug", form);
  const isRepresentative = isRepresentativeSlug(selectedSlug);

  // Owners already in the folder — a representative must point at one of these.
  const ownerOptions = (folder.subfolders ?? [])
    .filter((sf) => OWNER_SLUG_PREFIXES.some((prefix) => sf.slug.startsWith(prefix)))
    .map((sf, index) => ({
      value: sf.id,
      // Prefer the name from the owner's filled forms, then the metadata name, and
      // finally a positional fallback.
      label:
        getOwnerNameFromForms(sf) ||
        sf.metadata?.full_name?.trim() ||
        `Propietario ${index + 1}`,
    }));

  const onAdd: FormProps<FormValues>["onFinish"] = async ({
    template_slug,
    full_name,
    represents_subfolder_id,
  }) => {
    const normalizedFullName = full_name?.trim();

    const metadata: Record<string, any> = {};
    if (normalizedFullName) metadata.full_name = normalizedFullName;
    if (isRepresentativeSlug(template_slug) && represents_subfolder_id) {
      // Link the representative to the owner it represents (see cascade delete on the
      // backend). represents_name is denormalized so we can label the tab without a lookup.
      metadata.represents_subfolder_id = represents_subfolder_id;
      metadata.represents_name =
        ownerOptions.find((o) => o.value === represents_subfolder_id)?.label ?? "";
    }

    setSubmitting(true);
    try {
      // The endpoint returns the created subfolder (not the folder), so we refetch the
      // folder to refresh the store. Show success only AFTER that succeeds, to avoid a
      // success+error double toast if the refetch fails.
      await dispatch(
        addSubfolderToFolder({
          folderId: folder.id,
          template_slug,
          metadata,
        }),
      ).unwrap();

      const activeFolder = await dispatch(fetchFolderById(folder.id)).unwrap();
      await dispatch(setActiveFolderAsync(activeFolder));

      form.resetFields();
      messageApi.success("Participante adicional agregado al expediente.");
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo agregar el participante."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      {contextHolder}
      <div className="flex items-start gap-3 mb-3">
        <span className="flex items-center justify-center w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 shrink-0">
          <Users className="w-5 h-5" />
        </span>
        <div>
          <h3 className="font-semibold text-slate-800 leading-tight">
            Personas adicionales
          </h3>
          <p className="text-sm text-slate-500">
            ¿Más de un inquilino, fiador o propietario? Agrégalo aquí. Los
            representantes se ligan al propietario que representan.
          </p>
        </div>
      </div>
      <Form
        name="participant-form"
        form={form}
        onFinish={onAdd}
        className="w-full mx-auto"
      >
        <Form.Item<FormValues>
          label="Tipo de participante adicional"
          name="template_slug"
          labelCol={{ span: 24 }}
          rules={[{ required: true, message: "Campo requerido" }]}
        >
          <Select
            placeholder="Ej. Inquilino PF (adicional)"
            options={PARTICIPANT_OPTIONS}
            className="w-full"
          />
        </Form.Item>

        {isRepresentative && (
          <Form.Item<FormValues>
            label="¿A qué propietario representa?"
            name="represents_subfolder_id"
            labelCol={{ span: 24 }}
            rules={[{ required: true, message: "Selecciona el propietario" }]}
          >
            <Select
              placeholder={
                ownerOptions.length
                  ? "Selecciona un propietario"
                  : "Agrega primero un propietario"
              }
              options={ownerOptions}
              disabled={!ownerOptions.length}
              notFoundContent="No hay propietarios en el expediente"
              className="w-full"
            />
          </Form.Item>
        )}

        <Form.Item<FormValues>
          label="Nombre (opcional, para identificar la subcarpeta)"
          name="full_name"
          labelCol={{ span: 24 }}
        >
          <Input placeholder="Ej. Juan Pérez" />
        </Form.Item>

        <Form.Item>
          <Button
            loading={submitting}
            icon={<UserAddOutlined />}
            type="primary"
            htmlType="submit"
          >
            Agregar participante
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};
